import type { SVGProps } from 'react';
import type { NetworkNodeKind } from '@ni/shared';

/**
 * **How a record is drawn on a graph** — the one shape registry every graph in
 * the dashboard reads, so a person, an organization or an event looks the same
 * on the Network tab, the knowledge panel and the whole-graph view.
 *
 * **Kind is shape**: a circle for a person, a square for an organization, a
 * diamond for an event. Status is not registered here: a tracked person's mark
 * is filled in its status tone (`statusTone`, @ni/shared, through `[data-tone]`
 * in styles/status.css), the same table the People table's dot reads.
 */

/**
 * One record's mark, centred on (`x`, `y`) with half-width `r`. Every other
 * prop — the class, a `data-*` attribute, `vectorEffect` — goes on the shape.
 * The diamond is drawn a touch larger than the others, which is what makes the
 * three read as the same size: a diamond's area is half its bounding square's.
 */
export function GraphMark({
  kind,
  x = 0,
  y = 0,
  r,
  ...rest
}: { kind: NetworkNodeKind; x?: number; y?: number; r: number } & Omit<
  SVGProps<SVGElement>,
  'x' | 'y' | 'r'
>) {
  const props = rest as Record<string, unknown>;
  if (kind === 'org') {
    return (
      <rect {...props} x={x - r} y={y - r} width={r * 2} height={r * 2} rx={Math.min(2, r / 4)} />
    );
  }
  if (kind === 'event') {
    const d = r + 1;
    return (
      <polygon {...props} points={`${x},${y - d} ${x + d},${y} ${x},${y + d} ${x - d},${y}`} />
    );
  }
  return <circle {...props} cx={x} cy={y} r={r} />;
}
